import React, { useState, useEffect, useRef, ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import type { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { animated, useSpring } from '@react-spring/web';
import { actions as compDashboardActions } from '../../actions/companyDashboardActions';
import { actions as projectActions } from '../../actions/projectActions';
import * as types from '../../constants/actionTypes';
import Notification from '../Common/Notification';
import Routes from '../../constants/routes';
import { DashboardsTab } from '../../styles/shared-styles';
import MultiSelectBox from '../Common/MultiSelectBox/MultiSelectBox';
import Openings from './components/Openings/Openings';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import InputText from '@Common/InputText';
import TaskListOption from '../TaskList/TaskListOption';
import Modal61 from '@Common/Modal/Modal61';
import CustomizeWidgetsDrawer from '../CorporateDashboard/CustomizeWidgetsDrawer';
import { isRegularUser, isCustomerAdminOrOwner } from '../../helpers/accountHelper';
import { CORPORATE_DASHBOARD_DEFAULT, CORPORATE_DASHBOARD_DEFAULT_MANAGE, OVERVIEW_DEFAULT_TABS } from '../../constants/constants';
import { Modal61Ref } from '@Types/common';
import { Tab } from './components/types';
import ConfigModal from './Modals/ConfigModal';
import { useSafeAsyncEffect } from '@Components/Common/AbortableApi';
import './Overview.scss';

const { CORPORATE_DASHBOARD_VIEW_DEFAULT_ID, SAVE_CURRENT_VIEW_TEXT, SAVE_CURRENT_VIEW_ID } = CORPORATE_DASHBOARD_DEFAULT;
const { SAVE_CURRENT_VIEW_MANAGE_TEXT, SAVE_CURRENT_VIEW_MANAGE_ID } = CORPORATE_DASHBOARD_DEFAULT_MANAGE;

export interface ProjectOption {
    id: number;
    name: string;
    abbreviation: string;
    description: string;
}

const defaultSettings = {
    updates: 0, //this is used to track state changes from customize widget drawer so child components can know when to update without a deep compare
    isProjectSummariesActive: true,
    isLocationSummariesActive: true,
    isMyTasksDueSoonActive: true,
    isMilestonesMissedActive: true,
    brandIds: [],
    areaIds: [],
    regionIds: [],
    managementCompanyIds: [],
    locationTypeIds: [],
    continentIds: [],
    isMilestonesOnly: false,
    disciplineIds: [],
    departmentIds: [],
    phaseIds: [],
    communityIds: [],
    baselineIds: [],
    projectIds: [],
    locationIds: [],
    tagIds: [],
};

const defaultTabsConfig: Array<Tab> = [
    {
        id: OVERVIEW_DEFAULT_TABS.Openings.id,
        label: OVERVIEW_DEFAULT_TABS.Openings.label,
        component: <></>,
        icon: OVERVIEW_DEFAULT_TABS.Openings.icon,
        selectedProjectIds: [],
    },
    {
        id: OVERVIEW_DEFAULT_TABS.Projects.id,
        label: OVERVIEW_DEFAULT_TABS.Projects.label,
        component: <></>,
        icon: OVERVIEW_DEFAULT_TABS.Projects.icon,
        selectedProjectIds: [],
    },
    {
        id: OVERVIEW_DEFAULT_TABS.MyLocations.id,
        label: OVERVIEW_DEFAULT_TABS.MyLocations.label,
        component: <></>,
        icon: OVERVIEW_DEFAULT_TABS.MyLocations.icon,
        selectedProjectIds: [],
    },
];

const Overview = (): ReactElement => {
    const [tabs, setTabs] = useState<Array<Tab>>([]);
    const [activeTab, setActiveTab] = useState<Tab>(defaultTabsConfig[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterSettings, setFilterSettings] = useState({
        isSubscriptionProjectWidgetActive: false,
        accessibleLocationCount: 0,
        filters: {
            // location filters
            brands: [],
            areas: [],
            regions: [],
            locations: [],
            locationsByAbbreviation: [],
            managementCompanies: [],
            locationTypes: [],
            continents: [],
            //task filters
            disciplines: [],
            departments: [],
            phases: [],
            communities: [],
            baselines: [],
            projects: [],
            tags: [],
        },
        settings: [],
        selectedSettingsId: 'defaultId',
        defaultSettings: defaultSettings,
        selectedSettings: defaultSettings,
        matchingSettings: [],
    });
    const [isSettingsDrawerOpen, setIsSettingsDrawerOpen] = useState(false);
    const [availableProjects, setAvailableProjects] = useState<Array<ProjectOption>>([]);
    const [userFavoriteProjects, setUserFavoriteProjects] = useState<Array<ProjectOption>>([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const dispatch: Dispatch = useDispatch();
    const saveOverviewTab = async (overviewTabId?: number, openingsSubTabId?: number) =>
        dispatch(compDashboardActions.setOverviewTab(overviewTabId, openingsSubTabId));
    const loadDashboardFiltersV2 = async () => dispatch(compDashboardActions.loadDashboardFiltersV2());
    const loadDashboardSettingsV2 = async () => dispatch(compDashboardActions.loadDashboardSettingsV2());
    const updateSettingsV2 = async (settings, id) => dispatch(compDashboardActions.updateSettingsV2(settings, id));
    const saveSettingsV2 = async (settings) => dispatch(compDashboardActions.saveSettingsV2(settings));
    const checkSettingsAvailabilityV2 = async (settings) => dispatch(compDashboardActions.checkSettingsAvailabilityV2(settings));
    const getOverviewTabsConfig = async () => dispatch(compDashboardActions.getOverviewTabsConfig());
    const updateOverviewTabsConfig = async (config) => dispatch(compDashboardActions.updateOverviewTabsConfig(config));
    const loadProjectListFilters = async (signal) => dispatch(projectActions.loadProjectListFilters(signal));

    const saveFiltersModalRef = useRef<Modal61Ref>(null);
    const projectsConfigModalRef = useRef<Modal61Ref>(null);
    const { customerRole } = useSelector((state) => state.user);
    const history = useHistory();

    useSafeAsyncEffect(async (signal) => {
        document.title = 'Overview';

        const fetchData = async () => {
            setIsLoading(true);

            try {
                await Promise.all([loadFiltersV2(), loadSettingsV2(), loadTabsConfig(), loadUserFavoriteProjects(signal)]);
            } catch (error) {
                Notification('error', 'An Error Occurred', error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, []);

    // Apply default selection after all data is loaded
    useEffect(() => {
        if (isInitialLoad && availableProjects.length > 0 && tabs.length > 0) {
            const shouldApplyDefault = tabs.some((tab) => !tab.selectedProjectIds || tab.selectedProjectIds.length === 0);

            if (shouldApplyDefault) {
                const updatedTabs = tabs.map((tab) => {
                    if (!tab.selectedProjectIds || tab.selectedProjectIds.length === 0) {
                        let defaultProjectIds = [];

                        if (userFavoriteProjects.length > 0) {
                            // Priority 1: Use the first favorite project
                            defaultProjectIds = [userFavoriteProjects[0].id];
                        } else if (availableProjects.length > 0) {
                            // Priority 2: Use the first project from availableProjects as fallback
                            defaultProjectIds = [availableProjects[0].id];
                        }

                        const updatedTab = {
                            ...tab,
                            selectedProjectIds: defaultProjectIds,
                        };

                        return {
                            ...updatedTab,
                            component: buildTabComponent(updatedTab),
                        };
                    }
                    return tab;
                });

                setTabs(updatedTabs);

                // Update active tab if needed
                const activeTabIndex = updatedTabs.findIndex((t) => t.id === activeTab?.id);
                if (activeTabIndex !== -1) {
                    setActiveTab(updatedTabs[activeTabIndex]);
                }

                // Prevent re-running this initialization logic
                setIsInitialLoad(false);
            }
        }
    }, [isInitialLoad, availableProjects, userFavoriteProjects, tabs, buildTabComponent, activeTab, setTabs, setActiveTab]);

    const loadUserFavoriteProjects = async (signal) => {
        try {
            const { payload, type } = await loadProjectListFilters(signal);
            if (type === types.LOAD_PROJECT_FILTERS_SUCCESS) {
                // Extract favorite projects from the API response
                const favorites = payload.userFavoriteProjects || [];
                setUserFavoriteProjects(favorites);
            }
        } catch (error) {
            console.error('Error loading user favorite projects:', error);
        }
    };

    const loadFiltersV2 = async () => {
        try {
            const response = await loadDashboardFiltersV2();
            if (response.type === types.LOAD_COMPANY_DASHBOARD_FILTERS_SUCCESS) {
                setFilterSettings({
                    ...filterSettings,
                    isSubscriptionProjectWidgetActive: response.payload.isSubscriptionProjectWidgetActive,
                    accessibleLocationCount: response.payload.accessibleLocationCount,
                    filters: { ...filterSettings.filters, ...response.payload.filters },
                });
            } else if (response.type === types.LOAD_COMPANY_DASHBOARD_FILTERS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const loadSettingsV2 = async () => {
        try {
            const response = await loadDashboardSettingsV2();
            if (response.type === types.LOAD_COMPANY_DASHBOARD_SETTINGS_SUCCESS && response.payload) {
                const newSelectedSettingsId =
                    filterSettings.selectedSettingsId === 'defaultId' && response.payload[0].id
                        ? response.payload[0].id
                        : filterSettings.selectedSettingsId;

                setFilterSettings({
                    ...filterSettings,
                    settings: response.payload,
                    selectedSettingsId: newSelectedSettingsId,
                });

                onSettingsMenuChange(newSelectedSettingsId);
            } else if (response.type === types.LOAD_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const loadTabsConfig = async () => {
        try {
            const response = await getOverviewTabsConfig();
            if (response.type === types.GET_OVERVIEW_TABS_CONFIG_SUCCESS) {
                setAvailableProjects(response.payload.availableProjects);

                let tabsConfig = [...defaultTabsConfig];

                // Only override default tabs config if we have saved a config
                if (response.payload.overviewConfig.tabs) {
                    tabsConfig = defaultTabsConfig.map((defConfig) => {
                        const configTab = response.payload.overviewConfig.tabs.find((tab) => tab.id === defConfig.id);
                        return configTab ? { ...defConfig, ...configTab } : defConfig;
                    });
                }

                tabsConfig = tabsConfig.map((tab) => ({
                    ...tab,
                    component: buildTabComponent(tab),
                }));

                setTabs(tabsConfig);

                if (response.payload.selectedTabId) {
                    const selectedTab = tabsConfig.find((tab) => tab.id === response.payload.selectedTabId);

                    if (!selectedTab) {
                        throw new Error(`Unable to find tab configuration for tab id ${response.payload.selectedTabId}.`);
                    }

                    setActiveTab(selectedTab);
                }
            } else if (response.type === types.GET_OVERVIEW_TABS_CONFIG_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const onTabClick = async (tab: Tab) => {
        await saveOverviewTab(tab.id, undefined);

        setActiveTab(tab);
    };

    const onSettingsMenuChange = async (selectedSettingsId: string) => {
        const newSettings =
            filterSettings.settings.find((setting) => setting?.id === selectedSettingsId) ??
            // If no setting is found, set default settings
            filterSettings.defaultSettings;

        setFilterSettings({
            ...filterSettings,
            selectedSettings: newSettings,
            selectedSettingsId: selectedSettingsId,
        });
    };

    const getSettingsMenuOptions = () => {
        const options = filterSettings.settings.map((setting) => ({
            id: setting.id ?? CORPORATE_DASHBOARD_VIEW_DEFAULT_ID,
            name: setting.name ?? 'Unsaved View',
            itemIcon: 'list-check',
        }));

        const isEmptyList = options.find((option) => option.id === CORPORATE_DASHBOARD_VIEW_DEFAULT_ID);

        if (!isEmptyList) {
            options.push({ itemIcon: 'plus', id: CORPORATE_DASHBOARD_VIEW_DEFAULT_ID, name: 'Create new view', isSeparator: true });
        }

        options.push({ itemIcon: 'pen', id: SAVE_CURRENT_VIEW_MANAGE_ID, name: SAVE_CURRENT_VIEW_MANAGE_TEXT, isSeparator: !!isEmptyList });
        options.push({ itemIcon: 'floppy-disk', id: SAVE_CURRENT_VIEW_ID, name: SAVE_CURRENT_VIEW_TEXT, isSeparator: !!isEmptyList });

        return options;
    };

    const handleSettingsMenuChange = async (menuItemValue) => {
        if (menuItemValue === SAVE_CURRENT_VIEW_ID) {
            saveFiltersModalRef.current && saveFiltersModalRef.current.show();
        } else if (menuItemValue === SAVE_CURRENT_VIEW_MANAGE_ID) {
            history.push(Routes.ManageCorporateViews);
        } else {
            onSettingsMenuChange(menuItemValue);
        }
    };

    const onSaveCustomFilters = async () => {
        setIsLoading(true);
        const { selectedSettings } = filterSettings;
        if (selectedSettings.id == CORPORATE_DASHBOARD_VIEW_DEFAULT_ID) selectedSettings.id = undefined;

        try {
            let response, successType, failureType;
            if (selectedSettings.id) {
                response = await updateSettingsV2(selectedSettings, selectedSettings.id);
                successType = types.UPDATE_COMPANY_DASHBOARD_SETTINGS_SUCCESS;
                failureType = types.UPDATE_COMPANY_DASHBOARD_SETTINGS_FAILURE;
            } else {
                response = await saveSettingsV2(selectedSettings);
                successType = types.SAVE_COMPANY_DASHBOARD_SETTINGS_SUCCESS;
                failureType = types.SAVE_COMPANY_DASHBOARD_SETTINGS_FAILURE;
            }

            if (response.type === successType) {
                Notification('success', 'Settings Updated and Saved', '');
                await loadSettingsV2();
            } else if (response.type === failureType) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onCompleteCustomizerDrawer = (newSettings) => {
        setFilterSettings({
            ...filterSettings,
            selectedSettings: newSettings,
            selectedSettingsId: newSettings.id,
        });
    };

    const onCloseCustomizerDrawer = () => {
        setIsSettingsDrawerOpen(false);
    };

    const checkAvailability = async (settings) => {
        try {
            const response = await checkSettingsAvailabilityV2(settings);

            if (response.type === types.CHECK_COMPANY_DASHBOARD_SETTINGS_SUCCESS) {
                setFilterSettings({ ...filterSettings, matchingSettings: response.payload });
            } else if (response.type === types.CHECK_COMPANY_DASHBOARD_SETTINGS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        }
    };

    const openConfigModal = () => {
        projectsConfigModalRef.current?.show();
    };

    const handleMarkInitialLoadComplete = () => {
        setIsInitialLoad(false);
    };

    const renderConfigIcon = () => {
        // Config wheel can be seen by Moderators, Administrators, and Owner roles, but only Administrators and Owners can click on it
        if (isCustomerAdminOrOwner(customerRole)) {
            return <i className='mb-1 fa-regular fa-gear' onClick={() => openConfigModal()}></i>;
        } else if (!isRegularUser(customerRole)) {
            return <i className='mb-1 fa-regular fa-gear not-allowed'></i>;
        }
    };

    const buildTabComponent = (tab: Tab) => {
        switch (tab.id) {
            case OVERVIEW_DEFAULT_TABS.Openings.id: {
                const ids = Array.isArray(tab.selectedProjectIds) ? [...tab.selectedProjectIds] : [];
                const key = `openings-${tab.id}-${ids.join('-')}`;
                return <Openings key={key} projectIds={ids} />;
            }
            default:
                return <></>;
        }
    };

    const saveConfig = async (tabId: number, tabLabel: string, selectedProjectIds: number[]): Promise<void> => {
        setIsLoading(true);
        const config = {
            tabId: tabId,
            tabLabel: tabLabel,
            selectedProjectIds: selectedProjectIds,
        };

        try {
            const response = await updateOverviewTabsConfig(config);

            if (response.type === types.UPDATE_OVERVIEW_TABS_CONFIG_SUCCESS) {
                Notification('success', 'Settings Updated and Saved', '');

                const tabsConfig = [...tabs];
                const tabIndex = tabsConfig.findIndex((t) => t.id === tabId);

                if (tabIndex === -1) {
                    throw new Error(`Unable to find tab configuration for tab id ${tabId}.`);
                }

                const updatedTab = {
                    ...tabsConfig[tabIndex],
                    label: tabLabel,
                    selectedProjectIds: selectedProjectIds,
                };

                // Build component with updated tab data
                updatedTab.component = buildTabComponent(updatedTab);

                tabsConfig[tabIndex] = updatedTab;

                setTabs(tabsConfig);

                // Update activeTab if the saved tab is currently active
                if (activeTab?.id === tabId) {
                    setActiveTab(updatedTab);
                }
            } else if (response.type === types.UPDATE_OVERVIEW_TABS_CONFIG_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateView = filterSettings.selectedSettings.id && filterSettings.selectedSettings.id != CORPORATE_DASHBOARD_VIEW_DEFAULT_ID;

    const panelAnimation = useSpring({ transform: isSettingsDrawerOpen ? 'translateX(0%)' : 'translateX(100%)' });

    return (
        <LoadingIndicator isLoading={isLoading} hideWhileLoading={true} isFullscreen={true}>
            <animated.div
                style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    width: '380px',
                    height: '100vh',
                    background: '#eee',
                    boxShadow: '-2px 0 5px rgba(0, 0, 0, 0.2)',
                    padding: '20px',
                    zIndex: 999,
                    ...panelAnimation,
                }}>
                <CustomizeWidgetsDrawer
                    onComplete={onCompleteCustomizerDrawer}
                    onClose={onCloseCustomizerDrawer}
                    settings={filterSettings.selectedSettings}
                    defaultSettings={filterSettings.defaultSettings}
                    filters={filterSettings.filters}
                    isSubscriptionProjectWidgetActive={filterSettings.isSubscriptionProjectWidgetActive}
                    matchingSettings={filterSettings.matchingSettings}
                    checkAvailability={checkAvailability}
                />
            </animated.div>

            <div className='overview'>
                <div className='row g-0 align-items-end mb-4'>
                    <div className='col-6'>
                        <DashboardsTab className='nav nav-tabs'>
                            {tabs.map((tab) => (
                                <li key={tab.id} className='nav-item'>
                                    <a
                                        className={`nav-link ${activeTab?.id === tab.id ? 'active' : ''}`}
                                        onClick={() => onTabClick(tab)}
                                        type='button'>
                                        <i className={`fa-regular ${tab.icon}`} />
                                        <span>{tab.label}</span>
                                    </a>
                                </li>
                            ))}
                        </DashboardsTab>
                    </div>

                    <div className='col-2 nav nav-tabs pb-2 pe-4'>
                        <div className='input-group'>
                            <input className='form-control border-end-0 border rounded' type='search' placeholder='search' id='search' />
                            <span className='input-group-append search-icon'>
                                <button className='btn btn-outline-secondary bg-white border-start-0 border-bottom-0 border' type='button'>
                                    <i className='far fa-search'></i>
                                </button>
                            </span>
                        </div>
                    </div>

                    <div className='col-2 nav nav-tabs pb-2'>
                        <MultiSelectBox
                            name='filtersView'
                            idField='id'
                            valueField='name'
                            options={getSettingsMenuOptions()}
                            onChange={handleSettingsMenuChange}
                            selectedValues={filterSettings.selectedSettingsId}
                            isMulti={false}
                            isSearchable={false}
                            optionComponent={TaskListOption}
                            shouldShowIcons={false}
                            className='mb-0 w-100'
                        />
                    </div>

                    <div className='col-1 nav nav-tabs pb-2'>
                        <button
                            type='button'
                            className='btn'
                            onClick={() => {
                                setIsSettingsDrawerOpen(true);
                            }}>
                            <i className='fa-regular fa-bars-filter'></i>
                            <span className='ms-2 show-filters'>Filters</span>
                        </button>
                    </div>

                    <div className='col-1 nav nav-tabs pb-2 tab-context-actions fa-xl'>{renderConfigIcon()}</div>
                </div>

                <div className='row'>
                    <div className='col-12'>{tabs.find((tab) => tab.id === activeTab?.id)?.component}</div>
                </div>
            </div>

            {/* Modals */}

            <Modal61
                title='Save View'
                ref={saveFiltersModalRef}
                isCentered={true}
                size='md'
                body={
                    <form name='saveViewForm' noValidate>
                        <div className='form-group'>
                            <label htmlFor='custom-filters-name' className='title'>
                                Custom View Name
                            </label>
                            <InputText
                                name='custom-filters-name'
                                type='text'
                                value={filterSettings.selectedSettings.name}
                                onChange={(e) =>
                                    setFilterSettings({
                                        ...filterSettings,
                                        selectedSettings: { ...filterSettings.selectedSettings, ['name']: e.target.value },
                                    })
                                }
                                isRequired={true}
                                errorMessage='Custom View Name is required'
                                maxLength={20}
                                placeholder={updateView ? 'Would you like to rename this view?' : 'What would you like to name this view?'}
                            />
                            <small id='custom-view-name-help' className='form-text text-muted sub-title'>
                                Character Limit 20
                            </small>
                        </div>

                        {filterSettings.matchingSettings.length > 0 && (
                            <div className='alert alert-warning'>
                                <p>A view with the same filters already exists.</p>
                                <p>Are you sure you want to continue?</p>
                            </div>
                        )}
                    </form>
                }
                primaryButtonText={updateView ? 'Update' : 'Save'}
                onPrimaryClick={onSaveCustomFilters}
            />

            <ConfigModal
                modalRef={projectsConfigModalRef}
                tab={activeTab}
                availableProjects={availableProjects}
                userFavoriteProjects={userFavoriteProjects}
                isInitialLoad={isInitialLoad}
                onMarkInitialLoadComplete={handleMarkInitialLoadComplete}
                onSave={saveConfig}
            />
        </LoadingIndicator>
    );
};

export default Overview;
